/* eslint-disable prefer-const */
'use client';
import React, { useEffect, useMemo, useState } from 'react';
import dynamic from 'next/dynamic';
import dayjs from 'dayjs';

// Components
import Skeleton from '@/components/atoms/skeletons';
import Empty from '@/components/atoms/empty';
import Collapse from '@/components/atoms/collapse';
import Icon from '@/components/atoms/icon';
import IndicatorCollapseItems from '../home-economics/indicator-collapse-item';
import MapsPackage, { AreaVariant, slugMapping } from '../mapsPackage';
import ChartPackageSkeleton from '@/components/molecules/skeleton-component/chartPacakge';

// Interfaces & Types
import { ContextualIndicator, HomeTags } from '@/interfaces/home';

// Hooks & Utils
import { useGetTagChartData } from '@/helpers/useGetTagChartData';
import { hasUUID, processSlug, slugify } from '@/utils/funtion';
import { getTags } from '@/services/apis/modules/home';

// Styles
import './styles.scss';

// Dynamic Imports
const ChartPackage = dynamic(
  () => import('@/components/templates/chartPackage'),
  {
    ssr: false,
    loading: () => <ChartPackageSkeleton />,
  },
);

interface IProps {
  type: 'indicator' | 'industry_indicator' | 'local_indicator';
  mapData?: any;
  slug: string;
  screen: string;
}

// Helper Functions
const getUpdatedAt = (indicator: ContextualIndicator) => {
  return dayjs(
    indicator.localIndicator?.updatedAt ||
      indicator.indicator?.updatedAt ||
      indicator.industryIndicator?.updatedAt,
  );
};

const isRecentIndicator = (indicator: ContextualIndicator) => {
  const updatedAt = getUpdatedAt(indicator);
  return updatedAt.isAfter(dayjs().subtract(3, 'day'));
};

const getSelectedIndicator = (indicator: ContextualIndicator) => {
  return (
    indicator.indicator ||
    indicator.industryIndicator ||
    indicator.localIndicator
  );
};

export const IndicatorGroupSession: React.FC<IProps> = ({ slug, screen }) => {
  const [activeItem, setActiveItem] = useState({ groupKey: '0', itemId: '' });
  const [tags, setTags] = useState<HomeTags>();
  const [isLoading, setIsLoading] = useState(true);
  const [localSlug, setLocalSlug] = useState<AreaVariant>('VN');
  const [type, setType] = useState('');
  const [initialData, setInitialData] = useState({
    key: '',
    value: undefined as any,
  });
  const [selectedTime, setSelectedTime] = useState<string | undefined>();

  const { chartData, handleGetChardData, handleGetChartDataByRange, loading } =
    useGetTagChartData();
  useEffect(() => {
    const fetchTags = async () => {
      try {
        setIsLoading(true);
        const response = await getTags({ key: slug, screen });
        setTags(response?.data || []);
      } catch (err) {
        console.error('Error fetching tags:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchTags();
  }, [slug, screen]);

  useEffect(() => {
    if (!tags?.children?.length) return;

    try {
      const validGroup = tags.children.find(
        (group: HomeTags) => group.contextualIndicators?.length > 0,
      );

      if (!validGroup) return;

      const sortedIndicators = handleSortedIndicators(
        validGroup.contextualIndicators,
      );
      if (!sortedIndicators.length) return;

      const firstIndicator = sortedIndicators[0];
      const selectedIndicator = getSelectedIndicator(firstIndicator);
      if (!selectedIndicator) return;

      const newSlug = processSlug(validGroup.slug);
      setLocalSlug(slugMapping[newSlug] as AreaVariant);

      setActiveItem({
        groupKey: tags.children.indexOf(validGroup).toString(),
        itemId: selectedIndicator.id,
      });

      handleGetChardData(
        selectedIndicator.id,
        selectedIndicator.type,
        selectedIndicator.isStructure ? 1 : 0,
        '',
        selectedIndicator.isObjectOneTime ? 1 : 0,
      );
    } catch (error) {
      console.error('Error in useEffect:', error);
    }
  }, [tags]);

  useEffect(() => {
    const values = chartData?.values || {};
    for (const key in values) {
      if (Object.keys(values[key]).length > 0) {
        setInitialData({ key, value: values[key] });
      }
    }
  }, [chartData]);

  useEffect(() => {
    if (!selectedTime) return;

    handleGetChardData(
      activeItem.itemId,
      'local_indicator',
      chartData?.isStructure ? 1 : 0,
      selectedTime,
      chartData?.isObjectOneTime ? 1 : 0,
    );
  }, [selectedTime, activeItem.itemId, chartData]);

  const handleSelectTime = (value: string) => setSelectedTime(value);

  const handleSortedIndicators = (
    contextualIndicators: ContextualIndicator[],
  ) => {
    return [...contextualIndicators].sort((a, b) => {
      const aUpdatedAt = getUpdatedAt(a);
      const bUpdatedAt = getUpdatedAt(b);
      const threeDaysAgo = dayjs().subtract(3, 'day');

      const isARecent = aUpdatedAt.isAfter(threeDaysAgo);
      const isBRecent = bUpdatedAt.isAfter(threeDaysAgo);

      if (isARecent && !isBRecent) return -1;
      if (!isARecent && isBRecent) return 1;

      return bUpdatedAt.valueOf() - aUpdatedAt.valueOf();
    });
  };

  const handleItemClick = (
    item: HomeTags,
    indicator: ContextualIndicator,
    index: number,
  ) => {
    const selectedIndicator = getSelectedIndicator(indicator);
    if (!selectedIndicator) return;

    const newSlug = processSlug(item.slug || '');
    setLocalSlug(slugMapping[newSlug] as AreaVariant);

    setActiveItem({
      groupKey: index.toString(),
      itemId: selectedIndicator.id,
    });

    handleGetChardData(
      selectedIndicator.id,
      selectedIndicator.type,
      selectedIndicator.isStructure ? 1 : 0,
      '',
      selectedIndicator.isObjectOneTime ? 1 : 0,
    );

    setType(selectedIndicator.type);
  };

  const items = useMemo(() => {
    if (!tags?.children) return [];

    return tags.children.map((item: HomeTags, index: number) => {
      const sortedIndicators = handleSortedIndicators(
        item?.contextualIndicators || [],
      );
      const hasToday = sortedIndicators.some(isRecentIndicator);

      return {
        key: index.toString(),
        label: (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {item?.title}
            {hasToday && (
              <div className="collapse-header_new">
                <Icon iconName="newest" size="28x28" />
              </div>
            )}
          </div>
        ),
        children: (
          <>
            {!item?.contextualIndicators?.length ? (
              <Empty />
            ) : (
              sortedIndicators.map((indicator: ContextualIndicator) => (
                <IndicatorCollapseItems
                  key={indicator?.id}
                  area={localSlug}
                  activeItemId={activeItem?.itemId}
                  id={getSelectedIndicator(indicator)?.id}
                  slug={getSelectedIndicator(indicator)?.slug}
                  type={getSelectedIndicator(indicator)?.type}
                  name={getSelectedIndicator(indicator)?.name}
                  isStructure={getSelectedIndicator(indicator)?.isStructure}
                  isObjectOneTime={
                    getSelectedIndicator(indicator)?.isObjectOneTime
                  }
                  hasMap
                  onItemClick={() => handleItemClick(item, indicator, index)}
                  isToday={isRecentIndicator(indicator)}
                />
              ))
            )}
          </>
        ),
      };
    });
  }, [tags, activeItem.itemId]);

  const memoryChart = useMemo(() => {
    if (!chartData && isLoading) return <ChartPackageSkeleton />;

    if (
      initialData.key === 'localIndicator' &&
      (!!chartData?.values?.localIndicator?.map?.length ||
        !!initialData?.value?.map?.length)
    ) {
      return (
        <MapsPackage
          area={localSlug || 'VN'}
          id={chartData?.id}
          dataMaps={chartData?.values?.localIndicator?.map}
          original={chartData?.original}
          originalUrl={chartData?.originalUrl}
          unit={chartData?.unit}
          mapTitle={chartData?.name}
          timeData={chartData?.yearList}
          chartType={chartData?.chartType}
          onSelectTime={handleSelectTime}
        />
      );
    }

    return (
      <ChartPackage
        data={
          !chartData?.isStructure &&
          !chartData?.isObjectOneTime &&
          !chartData?.isTwoUnit
            ? !chartData?.values?.indicator?.children?.length ||
              !chartData?.values?.industryIndicator?.children?.length
              ? chartData?.values?.indicator?.often ||
                (!chartData?.values?.localIndicator?.map?.length
                  ? chartData?.values?.localIndicator?.chart
                  : chartData?.values?.localIndicator?.map) ||
                chartData?.values?.industryIndicator?.often
              : chartData?.values?.indicator?.children
            : chartData?.isStructure
              ? chartData?.values?.indicator?.children?.response ||
                chartData?.values?.industryIndicator?.children?.response ||
                (!chartData?.values?.localIndicator?.map?.length
                  ? chartData?.values?.localIndicator?.chart
                  : chartData?.values?.localIndicator?.map)
              : chartData?.values?.indicator?.children ||
                chartData?.values?.industryIndicator?.children ||
                chartData?.values?.indicator?.often ||
                chartData?.values?.industryIndicator?.often ||
                (!chartData?.values?.localIndicator?.map?.length
                  ? chartData?.values?.localIndicator?.chart
                  : chartData?.values?.localIndicator?.map)
        }
        loading={loading}
        isStructure={chartData?.isStructure}
        name={chartData?.name}
        slug={slugify(chartData?.name)}
        id={chartData?.id}
        original={chartData?.original}
        originalUrl={chartData?.originalUrl}
        unit={chartData?.unit}
        isObjectOneTime={chartData?.isObjectOneTime}
        handleClickRangeTime={(item) => {
          const type =
            Object.keys(chartData?.values?.indicator || {}).length > 0
              ? 'indicator'
              : Object.keys(chartData?.values?.industryIndicator || {}).length >
                  0
                ? 'industry_indicator'
                : 'local_indicator';
          handleGetChartDataByRange(item, type, chartData?.isStructure ? 1 : 0);
        }}
        dataType={chartData?.dataType}
        chartType={chartData?.chartType || 'column'}
        typeData={
          Object.keys(chartData?.values?.indicator || {}).length > 0
            ? 'indicator'
            : Object.keys(chartData?.values?.industryIndicator || {}).length > 0
              ? 'industry_indicator'
              : 'local_indicator'
        }
        parentId={chartData?.parentId}
        chartHeight={670}
      />
    );
  }, [chartData, initialData, localSlug, loading, isLoading]);

  return (
    <div className="indicator-session-group" id={slug}>
      <div className="indicator-session-group-title">
        {isLoading || tags?.title}
      </div>
      {isLoading || <div className="line"></div>}
      {isLoading ? (
        <Skeleton active type="default" loading paragraph={{ rows: 3 }} />
      ) : (
        <div className="indicator-session-group-content">
          <React.Fragment>
            {!tags?.children?.length ? (
              <Empty />
            ) : (
              <Collapse items={items} accordion={true} />
            )}
          </React.Fragment>
          <div className="indicator-session-group-chart">{memoryChart}</div>
        </div>
      )}
    </div>
  );
};
